import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Button } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Modal } from '@televet/kibble-ui/Modal';
import { Select } from '@televet/kibble-ui/Select';
import { Switch } from '@televet/kibble-ui/Switch';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Textarea } from '@televet/kibble-ui/Textarea';
import { Box, Flex, Table, VStack } from '@televet/kibble-ui/chakra';
import { InsightEmailPreview } from '@/features/agent-insights/InsightEmailPreview';
import { PageContainer } from '@/components/PageContainer';
import { toaster } from '@/components/ui/toaster';
import { trpc } from '@/lib/trpc';

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'info', label: 'Info' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

type Severity = 'info' | 'low' | 'medium' | 'high';

function severityVariant(
  severity: Severity
): 'default' | 'info' | 'warning' | 'error' {
  switch (severity) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'info';
    case 'info':
    default:
      return 'default';
  }
}

export const Route = createFileRoute('/_authenticated/agent-insights/')({
  beforeLoad: () => ({ pageTitle: 'Agent Insights' }),
  loader: () => ({ pageTitle: 'Agent Insights' }),
  component: AgentInsights,
});

function AgentInsights() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [clinicIdFilter, setClinicIdFilter] = useState('');
  const [executionIdFilter, setExecutionIdFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState<Severity | ''>('');
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [selectedInsightId, setSelectedInsightId] = useState<string | null>(
    null
  );

  const listQueryInput = useMemo(
    () => ({
      page,
      limit: 15,
      where: {
        ...(clinicIdFilter ? { clinicId: clinicIdFilter } : {}),
        ...(executionIdFilter ? { executionId: executionIdFilter } : {}),
        ...(severityFilter ? { severity: severityFilter as Severity } : {}),
      },
    }),
    [page, clinicIdFilter, executionIdFilter, severityFilter]
  );

  const { data, isLoading } = useQuery(
    trpc.agentInsight.list.queryOptions(listQueryInput)
  );

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        <Flex justify="space-between" align="center" gap={4}>
          <Flex gap={3} align="center" flex="1">
            <TextInput
              placeholder="Filter by clinicId..."
              value={clinicIdFilter}
              onChange={(e) => {
                setClinicIdFilter(e.target.value);
                setPage(1);
              }}
              maxW="280px"
            />
            <TextInput
              placeholder="Filter by executionId..."
              value={executionIdFilter}
              onChange={(e) => {
                setExecutionIdFilter(e.target.value);
                setPage(1);
              }}
              maxW="280px"
            />
            <Select
              options={SEVERITY_OPTIONS}
              value={[severityFilter]}
              onValueChange={(details) => {
                setSeverityFilter((details.value[0] as Severity | '') ?? '');
                setPage(1);
              }}
              placeholder="Severity"
            />
          </Flex>
          <Button onClick={() => setIsGenerateOpen(true)}>
            Generate Insight
          </Button>
        </Flex>

        <Flex flex="1 1 auto" justify="center" align="flex-start">
          <Card
            flex="1 1 auto"
            direction="column"
            borderColor="border.default"
            rounded="lg"
            overflow="hidden"
          >
            <Box overflowX="auto" position="relative" width="100%">
              <Table.Root size="md" width="100%" minWidth="900px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader minWidth="180px" whiteSpace="nowrap">
                      Created
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="160px" whiteSpace="nowrap">
                      Type
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="120px" whiteSpace="nowrap">
                      Severity
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="240px" whiteSpace="nowrap">
                      Title
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="220px" whiteSpace="nowrap">
                      Execution ID
                    </Table.ColumnHeader>
                    <Table.ColumnHeader minWidth="220px" whiteSpace="nowrap">
                      Email Subject
                    </Table.ColumnHeader>
                    <Table.ColumnHeader
                      textAlign="end"
                      width="100px"
                      minWidth="100px"
                      position="sticky"
                      right="0"
                      bg="bg"
                      zIndex={1}
                      borderLeft="1px solid"
                      borderColor="border.default"
                      whiteSpace="nowrap"
                    >
                      Actions
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {isLoading && (
                    <Table.Row>
                      <Table.Cell colSpan={7}>
                        <Text color="text.subtle">Loading…</Text>
                      </Table.Cell>
                    </Table.Row>
                  )}
                  {!isLoading && data?.insights.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={7}>
                        <Text color="text.subtle">
                          No insights match the current filters.
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  )}
                  {data?.insights.map((insight) => (
                    <Table.Row key={insight.id}>
                      <Table.Cell whiteSpace="nowrap">
                        {new Date(insight.createdAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        {insight.insightType}
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        <Tag
                          label={insight.severity}
                          variant={severityVariant(insight.severity as Severity)}
                        />
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        {insight.title}
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        <Text
                          color={insight.executionId ? undefined : 'text.subtle'}
                        >
                          {insight.executionId ?? '—'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell whiteSpace="nowrap">
                        <Text
                          color={insight.emailSubject ? undefined : 'text.subtle'}
                        >
                          {insight.emailSubject ?? '—'}
                        </Text>
                      </Table.Cell>
                      <Table.Cell
                        textAlign="end"
                        position="sticky"
                        right="0"
                        bg="bg"
                        zIndex={1}
                        borderLeft="1px solid"
                        borderColor="border.default"
                      >
                        <Button
                          size="sm"
                          variant="secondary"
                          iconName="eyeOpen"
                          onClick={() => setSelectedInsightId(insight.id)}
                        >
                          View
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Card>
        </Flex>

        {data?.pagination?.total ? (
          <Flex justify="space-between" align="center" gap={4} wrap="wrap" px={2}>
            <Text fontSize="sm" variant="subtle">
              {data.pagination.total} insight
              {data.pagination.total !== 1 ? 's' : ''} total
            </Text>
            <Flex gap={3} align="center">
              <Button
                size="sm"
                disabled={!data.pagination.hasPrev}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Text fontSize="sm" variant="subtle" minWidth="fit-content">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </Text>
              <Button
                size="sm"
                disabled={!data.pagination.hasNext}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        ) : undefined}
      </Flex>

      <InsightDetailDialog
        insight={
          selectedInsightId
            ? (data?.insights.find((i) => i.id === selectedInsightId) ?? null)
            : null
        }
        onClose={() => setSelectedInsightId(null)}
      />

      <GenerateInsightDialog
        isOpen={isGenerateOpen}
        onClose={() => setIsGenerateOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: [['agentInsight', 'list']],
          });
        }}
      />
    </PageContainer>
  );
}

type InsightDetail = {
  id: string;
  createdAt: string | Date;
  clinicId: string;
  insightType: string;
  title: string;
  emailSubject: string | null;
  severity: string;
  executionId: string | null;
  kpiMetrics: unknown;
  kpiSummary: string | null;
  headline: unknown;
  ctaText: string | null;
  recommendedNextAction: string | null;
  expiresAt: string | Date | null;
  agentConversationId: string;
  agentConversation: {
    id: string;
    currentAgent: { id: string; name: string };
  };
};

type InsightDetailDialogProps = {
  insight: InsightDetail | null;
  onClose: () => void;
};

function InsightDetailDialog({ insight, onClose }: InsightDetailDialogProps) {
  return (
    <Modal
      open={!!insight}
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      header={insight ? insight.title : 'Insight'}
      size="lg"
      footerProps={{
        submitText: 'Close',
        onSubmit: onClose,
        hideCancel: true,
      }}
    >
      {insight && (
        <VStack gap={4} align="stretch">
          <Flex gap={3} wrap="wrap">
            <Tag
              label={insight.severity}
              variant={severityVariant(insight.severity as Severity)}
            />
            <Tag label={insight.insightType} variant="default" />
          </Flex>

          <DetailRow label="Clinic ID" value={insight.clinicId} />
          <DetailRow
            label="Agent"
            value={insight.agentConversation.currentAgent.name}
          />
          <DetailRow
            label="Created"
            value={new Date(insight.createdAt).toLocaleString()}
          />
          {insight.expiresAt && (
            <DetailRow
              label="Expires"
              value={new Date(insight.expiresAt).toLocaleString()}
            />
          )}
          {insight.executionId && (
            <DetailRow label="Execution ID" value={insight.executionId} />
          )}

          <Flex direction="column" gap={2}>
            <Text fontWeight="medium" fontSize="sm">
              Email preview
            </Text>
            <InsightEmailPreview insightId={insight.id} />
          </Flex>
        </VStack>
      )}
    </Modal>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex gap={3} align="baseline">
      <Text fontWeight="medium" fontSize="sm" minW="120px" color="text.subtle">
        {label}
      </Text>
      <Text fontSize="sm">{value}</Text>
    </Flex>
  );
}

type GenerateInsightDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

function GenerateInsightDialog({
  isOpen,
  onClose,
  onSuccess,
}: GenerateInsightDialogProps) {
  const [clinicId, setClinicId] = useState('');
  const [promptTemplateId, setPromptTemplateId] = useState('');
  const [contextJson, setContextJson] = useState('');
  const [contextError, setContextError] = useState('');
  // Defaults to a dry run (no email). Emailing clinic staff is an explicit
  // opt-in — the switch must be turned on to set dryRun: false.
  const [dryRun, setDryRun] = useState(true);

  const { data: promptTemplateData } = useQuery(
    trpc.promptTemplate.list.queryOptions({
      where: { isPublished: true },
    })
  );

  const promptTemplateOptions = useMemo(
    () =>
      promptTemplateData?.promptTemplates.map((template) => ({
        value: template.id,
        label: template.prompt,
      })) ?? [],
    [promptTemplateData]
  );

  const reset = () => {
    setClinicId('');
    setPromptTemplateId('');
    setContextJson('');
    setContextError('');
    setDryRun(true);
  };

  const { mutate: generate, isPending } = useMutation(
    trpc.agentInsight.generate.mutationOptions({
      onSuccess: (result) => {
        toaster.success({
          title: 'Insight generation queued',
          description: `executionId: ${result.executionId}`,
        });
        reset();
        onClose();
        onSuccess();
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to queue insight generation',
          description: error.message,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const parseContext = (): Record<string, unknown> | null => {
    if (!contextJson.trim()) {
      return {};
    }
    try {
      const parsed = JSON.parse(contextJson);
      if (
        parsed === null ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed)
      ) {
        setContextError('Context must be a JSON object');
        return null;
      }
      setContextError('');
      return parsed as Record<string, unknown>;
    } catch (error) {
      setContextError(
        `Invalid JSON: ${error instanceof Error ? error.message : 'parse error'}`
      );
      return null;
    }
  };

  const handleSubmit = () => {
    const parsedContext = parseContext();
    if (parsedContext === null) return;
    generate({
      clinicId: clinicId.trim(),
      promptTemplateId,
      context: parsedContext,
      dryRun,
    });
  };

  const isSubmitDisabled =
    !clinicId.trim() || !promptTemplateId || isPending || !!contextError;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) {
          reset();
          onClose();
        }
      }}
      header="Generate Insight"
      size="md"
      footerProps={{
        submitText: 'Queue Generation',
        cancelText: 'Cancel',
        onSubmit: handleSubmit,
        onCancel: () => {
          reset();
          onClose();
        },
        loading: isPending,
        loadingText: 'Queueing...',
        disabled: isSubmitDisabled,
      }}
    >
      <VStack gap={4} align="stretch">
        <TextInput
          label="Clinic ID"
          required
          placeholder="cuid of the clinic in flowDB"
          value={clinicId}
          onChange={(e) => setClinicId(e.target.value)}
        />
        <Select
          label="Prompt Template"
          required
          placeholder="Select a published prompt template..."
          options={promptTemplateOptions}
          value={promptTemplateId ? [promptTemplateId] : []}
          onValueChange={(details) =>
            setPromptTemplateId(details.value[0] ?? '')
          }
        />
        <Flex direction="column" gap={2}>
          <Text fontWeight="medium" fontSize="sm">
            Context (optional JSON)
          </Text>
          <Textarea
            placeholder='e.g. {"runWindow": "2026-05"}'
            value={contextJson}
            onChange={(e) => setContextJson(e.target.value)}
            rows={4}
          />
          {contextError && (
            <Text fontSize="sm" color="text.danger">
              {contextError}
            </Text>
          )}
        </Flex>
        <Flex direction="column" gap={1}>
          <Switch
            checked={!dryRun}
            onCheckedChange={(details) => setDryRun(!details.checked)}
          >
            Send to clinic staff
          </Switch>
          <Text fontSize="sm" color="text.subtle">
            Off (default) only generates the insight. Turn on to email it to all
            active clinic staff once generated.
          </Text>
        </Flex>
      </VStack>
    </Modal>
  );
}
